import React, { useState, useRef, useEffect, ReactNode } from 'react'
import ReactDOM from 'react-dom'
import { fullscreenPortalHost } from 'components/answers/videoFullscreen'
import cn from 'classnames'
import styles from '../upvalue.module.scss'
import { BubbleWaveEffect } from '../effects/BubbleWaveEffect'
import { CrystalShardEffect } from '../effects/CrystalShardEffect'
import { BubbleGridEffect } from '../effects/BubbleGridEffect'

interface UpvalueEffectProps {
    children: ReactNode
    containerRef?: React.RefObject<HTMLDivElement>
    effectEnabled?: boolean
}

// You can switch between effects here
const CURRENT_EFFECT = BubbleGridEffect // BubbleGridEffect, BubbleWaveEffect, or CrystalShardEffect

const effectName =
    CURRENT_EFFECT === BubbleGridEffect
        ? 'bubbleGrid'
        : CURRENT_EFFECT === BubbleWaveEffect
        ? 'bubbleWave'
        : 'crystalShard'

export function UpvalueEffect({
    children,
    containerRef,
    effectEnabled = true,
}: UpvalueEffectProps) {
    const [clickPower, setClickPower] = useState(0)
    const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 })
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const timeRef = useRef(0)
    const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current)
        }
    }, [])

    // Apply glow to container
    useEffect(() => {
        if (containerRef?.current && clickPower > 0 && effectEnabled) {
            containerRef.current.classList.add(styles.containerGlow)
            containerRef.current.style.setProperty('--click-power', String(clickPower))
        }

        return () => {
            if (containerRef?.current) {
                containerRef.current.classList.remove(styles.containerGlow)
            }
        }
    }, [clickPower, containerRef, effectEnabled])

    const handleEffectTrigger = (element: HTMLElement | null) => {
        // If effects are disabled, don't do anything
        if (!effectEnabled) return

        if (element) {
            const rect = element.getBoundingClientRect()
            const centerX = rect.left + rect.width / 2
            const centerY = rect.top + rect.height / 2
            setButtonPosition({ x: centerX, y: centerY })
        }

        // Increase click power
        setClickPower(prev => Math.min(prev + 1, 50)) // Increased cap from 12 to 50

        // Clear existing timeout
        if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current)

        // Set new timeout to clear effects after inactivity
        fadeTimeoutRef.current = setTimeout(() => {
            // Rapidly decrease click power for quick snap back
            const decreasePower = setInterval(() => {
                setClickPower(prev => {
                    if (prev <= 0) {
                        clearInterval(decreasePower)
                        return 0
                    }
                    // Faster decay for text, accelerating as it gets smaller
                    const decayRate = prev > 8 ? 3 : prev > 4 ? 2 : 1
                    return Math.max(0, prev - decayRate)
                })
            }, 30) // Much faster interval for snappy return
        }, 400) // Shorter delay before starting return animation
    }

    return (
        <>
            {clickPower > 0 &&
                effectEnabled &&
                typeof document !== 'undefined' &&
                ReactDOM.createPortal(
                    <div
                        className={styles.effectContainer}
                        style={{
                            position: 'fixed',
                            left: `${buttonPosition.x}px`,
                            top: `${buttonPosition.y}px`,
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                            zIndex: 9999,
                        }}
                    >
                        <canvas
                            ref={canvasRef}
                            className={cn(styles.effectCanvas, styles[`${effectName}Canvas`])}
                            style={{
                                width: `${400 + clickPower * 30}px`,
                                height: `${400 + clickPower * 30}px`,
                                padding: '0',
                                margin: '0',
                            }}
                        />
                        <CURRENT_EFFECT
                            clickPower={clickPower}
                            buttonPosition={buttonPosition}
                            canvasRef={canvasRef}
                            timeRef={timeRef}
                            onAnimationFrame={() => {}}
                        />
                    </div>,
                    fullscreenPortalHost(),
                )}
            {React.Children.map(children, child => {
                if (
                    React.isValidElement<{
                        onEffectTrigger?: typeof handleEffectTrigger
                        clickPower?: number
                    }>(child)
                ) {
                    return React.cloneElement(child, {
                        onEffectTrigger: handleEffectTrigger,
                        clickPower,
                    })
                }
                return child
            })}
        </>
    )
}
